import Icon, {IconProps, IconSize} from '@/components/Icon';
import Text, {TextColor, TextType} from '@/components/Text';
import {
    DocumentSvg,
    EmptyPageSvg,
    ExternalLoadErrorSvg,
    ExternalNoPowerSvg,
    ExternalNotFoundSvg,
    LoadErrorSvg,
    NoSearchSvg,
} from '@/icons';
import DefaultSvg from '@/assets/svg/tocDefaultSvg.svg?tsx';
import classNames from 'classnames';
import styles from './index.module.scss';
import Button, {ButtonColor, ButtonSize, ButtonType} from '@/components/Button';

export enum ErrorType {
    // 用户账号未授权
    UserForbidden = 1,
    // 没有文档权限
    DocNoPermission = 2,
    // 文档页404
    DocNotFound = 3,
    // 文档加载失败
    DocRetry = 4,
    // 没有知识库权限
    RepoNoPermission = 5,
    // 知识库404
    RepoNotFound = 6,
    // 知识库加载失败
    RepoRetry = 7,
    // 暂无数据
    NoData = 8,
}


interface PlaceholderIconProps extends IconProps {
    type: ErrorType;
    className?: string;
    onRetry?: () => void;
}

export default function TocError(props: PlaceholderIconProps) {
    const {
        className,
        size = IconSize.Auto,
        type,
        onRetry,
        ...iconProps
    } = props;

    function getSvgByCode() {
        if ([
            ErrorType.UserForbidden,
            ErrorType.DocNoPermission,
        ].includes(type)) {
            return DefaultSvg;
        } else if ([
            ErrorType.DocNotFound,
        ].includes(type)) {
            return ExternalNotFoundSvg;
        } else if ([
            ErrorType.DocRetry,
            ErrorType.RepoRetry,
        ].includes(type)) {
            return ExternalLoadErrorSvg;
        }
        return ExternalNotFoundSvg;
    }

    function getContentByCode() {
        if (type === ErrorType.UserForbidden) {
            return <span>
                你的百度账号暂未被授权访问知识库，请
                <span className={styles.strongLink}>联系你的对接人进行授权。</span>点此查看
            </span>;
        }
        if (type === ErrorType.DocNotFound) {
            return '无法找到页面，此页面不存在或已被删除';
        }
        if (type === ErrorType.DocNoPermission) {
            return <span>
                暂无该页面权限，请<span className={styles.strongLink}>联系链接发送方</span>添加权限或查看
            </span>;
        }
        if (type === ErrorType.RepoNoPermission) {
            return <span>
                暂无该页面权限，请<span className={styles.strongLink}>联系链接发送方</span>添加权限或查看
            </span>;
        }
        if (type === ErrorType.RepoNotFound) {
            return '无法找到知识库，此知识库不存在或已被删除';
        }
        if (type === ErrorType.DocRetry) {
            return '当前页面出错，请刷新页面，或检查页面是否被删除';
        }
        if (type === ErrorType.RepoRetry) {
            return '知识库加载失败，';
        }
        return '暂无数据';
    }

    return (
        <div className={classNames(styles.root, className)}>
            <div className={styles.content}>
                <Icon size={size} svg={getSvgByCode()} {...iconProps} />
                <div className={styles.textArea}>
                    <Text type={TextType.T12} color={TextColor.Info} className={styles.textWrap}>
                        {getContentByCode()}
                    </Text>
                    {type === ErrorType.RepoRetry && (
                        <Button
                            type={ButtonType.Plain}
                            color={ButtonColor.Primary}
                            size={ButtonSize.XS}
                            onClick={onRetry}
                        >
                            点击重试
                        </Button>
                    )}
                    {(type === ErrorType.DocNoPermission || type === ErrorType.RepoNoPermission) && (
                        <a
                            target="_blank"
                            href={`${window.location.origin}/toc/permissionHelp.html`}
                            rel="noreferrer"
                            className={styles.linkSty}
                        >
                            <Icon svg={DocumentSvg} />
                            <span>如何获得知识库权限</span>
                        </a>
                    )}
                    {type === ErrorType.UserForbidden && (
                        <a
                            target="_blank"
                            href={`${window.location.origin}/toc/authHelp.html`}
                            rel="noreferrer"
                            className={styles.linkSty}
                        >
                            <Icon svg={DocumentSvg} />
                            <span>外部知识库账号授权流程</span>
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}
